import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  getChatChannels, getChatMessages, sendChatMessage, getChatMembers, deleteChatMessage,
  createChatChannel, updateChatChannel, deleteChatChannel, getChatUsers, addChatMember, removeChatMember,
  uploadChatMedia, muteChatMember, pinChatMessage,
  createChatPoll, voteChatPoll, reactChatMessage, searchChatMessages, markChatRead, openChatDm, setChatNotifMode,
  chatWsUrl, getMe, type ChatChannel, type ChatMessage, type ChatMember, type ChatPoll,
} from './api';
import AudienceTree from './AudienceTree';

function chanName(c: ChatChannel) { return c.kind === 'dm' ? (c.dm_peer?.display_name || 'Message privé') : c.kind === 'support' ? (c.guest_name || 'Visiteur') : c.name; }
function chanEmoji(c: ChatChannel) { return c.emoji || (c.kind === 'channel' ? '#' : (c.kind === 'dm' || c.kind === 'support') ? '' : '👥'); }
function chanAvatar(c: ChatChannel) { return c.kind === 'dm' ? c.dm_peer?.avatar_url : c.kind === 'support' ? c.guest_avatar : undefined; }

const REACT_EMOJIS = ['👍', '❤️', '😂', '🔥', '🎉', '😮', '😢', '👏'];

const EMOJIS = ['😀','😂','🥰','😍','😎','🤩','😢','😡','👍','👏','🙏','💪','🔥','💜','💖','🎉','🎮','🏆','💎','🪙','✨','👀','🤔','😴','🚀','⭐','❤️','😭','🤣','😅'];
const PURPLE = '#7c3aed';
const AXO_IMG = '/vitrine-assets/axo.png?v=2';

function initials(name?: string) { return (name || '?').replace(/[^\p{L}\p{N} ]/gu, '').trim().slice(0, 2).toUpperCase() || '?'; }
function toggleArr(arr: string[], x: string) { return arr.includes(x) ? arr.filter(a => a !== x) : [...arr, x]; }

function PollView({ poll, onVote }: { poll: ChatPoll; onVote: (optionId: number) => void }) {
  return (
    <div style={{ minWidth: 220 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontWeight: 600, color: '#0f172a' }}><span>📊</span>{poll.question}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {poll.options.map(o => {
          const pct = poll.total ? Math.round((o.votes / poll.total) * 100) : 0;
          const mine = poll.my_vote === o.id;
          return (
            <button key={o.id} onClick={() => onVote(o.id)} style={{ textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: mine ? '#7c3aed' : '#475569', fontWeight: mine ? 700 : 500, marginBottom: 2 }}>
                <span>{mine ? '✓ ' : ''}{o.text}</span><span>{pct}%</span>
              </div>
              <div style={{ height: 7, borderRadius: 4, background: '#ede9fe' }}><div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: mine ? '#7c3aed' : '#a78bfa', transition: 'width 0.3s' }} /></div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 7 }}>{poll.total} vote{poll.total > 1 ? 's' : ''} · sondage</div>
    </div>
  );
}
function timeFmt(s: string) {
  const d = new Date((s || '').replace(' ', 'T') + (s && s.includes('Z') ? '' : 'Z'));
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function linkify(text: string): React.ReactNode {
  const parts = text.split(/(https?:\/\/[^\s]+|@[A-Za-z0-9._]{2,})/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p))
      return <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed', wordBreak: 'break-all' }}>{p}</a>;
    if (/^@[A-Za-z0-9._]{2,}$/.test(p))
      return <span key={i} style={{ color: '#7c3aed', fontWeight: 600, background: '#f5f3ff', borderRadius: 4, padding: '0 3px' }}>{p}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function MediaView({ msg }: { msg: ChatMessage }) {
  if (!msg.media_url) return null;
  const url = msg.media_url;
  if (msg.media_type === 'image')
    return <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={msg.media_name || ''} style={{ maxWidth: 260, maxHeight: 240, borderRadius: 8, display: 'block', cursor: 'pointer' }} /></a>;
  if (msg.media_type === 'video')
    return <video src={url} controls style={{ maxWidth: 280, maxHeight: 260, borderRadius: 8, display: 'block' }} />;
  if (msg.media_type === 'audio')
    return <audio src={url} controls style={{ width: 240 }} />;
  return <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7c3aed', textDecoration: 'none', padding: '8px 10px', border: '1px solid #ddd6fe', borderRadius: 8, background: '#f5f3ff' }}>📎 {msg.media_name || 'Fichier'}</a>;
}

export default function NSLiveChat({ isAdmin, isMobile }: { isAdmin: boolean; isMobile?: boolean }) {
  const [me, setMe] = useState<{ user_id: number; role: string } | null>(null);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [members, setMembers] = useState<ChatMember[]>([]);
  const [online, setOnline] = useState<number[]>([]);
  const [typing, setTyping] = useState<Record<number, { name: string; t: number }>>({});
  const [text, setText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showDm, setShowDm] = useState(false);
  const [newGroupOnly, setNewGroupOnly] = useState(false);
  const [newMenu, setNewMenu] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [mobileConv, setMobileConv] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState<ChatMessage[]>([]);
  const [reactFor, setReactFor] = useState<number | null>(null);
  const [notifMenu, setNotifMenu] = useState(false);
  const [mention, setMention] = useState<{ q: string; start: number } | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastTypingSent = useRef(0);

  const active = channels.find(c => c.id === activeId) || null;
  const myMember = members.find(m => m.user_id === me?.user_id);
  const canPost = !active ? false : (isAdmin || active.post_policy === 'all' || myMember?.member_role === 'moderator');

  useEffect(() => { getMe().then(r => setMe({ user_id: (r.data as any).user_id, role: (r.data as any).role })).catch(() => {}); }, []);

  const loadChannels = useCallback(() => { getChatChannels().then(r => setChannels(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadChannels(); }, [loadChannels]);

  useEffect(() => {
    if (!activeId) { setMessages([]); setMembers([]); return; }
    getChatMessages(activeId).then(r => {
      setMessages(r.data);
      const last = r.data[r.data.length - 1];
      if (last) { markChatRead(activeId, last.id).catch(() => {}); setChannels(cs => cs.map(c => c.id === activeId ? { ...c, unread: 0 } : c)); }
    }).catch(() => {});
    getChatMembers(activeId).then(r => setMembers(r.data)).catch(() => {});
  }, [activeId]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  useEffect(() => {
    let stop = false;
    const connect = () => {
      if (stop) return;
      const cur = wsRef.current;
      if (cur && cur.readyState <= WebSocket.OPEN) return;   // déjà en cours / connectée
      const ws = new WebSocket(chatWsUrl());
      wsRef.current = ws;
      ws.onopen = () => { loadChannels(); };                 // rattrape les messages manqués à la (re)connexion
      ws.onerror = () => { try { ws.close(); } catch { /* onclose → reconnexion */ } };
      ws.onmessage = ev => {
        let d: any; try { d = JSON.parse(ev.data); } catch { return; }
        if (d.type === 'message') {
          const isActive = d.channel_id === activeIdRef.current;
          if (isActive) {
            setMessages(prev => prev.some(m => m.id === d.message.id) ? prev : [...prev, d.message]);
            markChatRead(d.channel_id, d.message.id).catch(() => {});
          }
          setChannels(cs => {
            if (!cs.some(c => c.id === d.channel_id)) { loadChannels(); return cs; }  // conversation neuve (visiteur) → on recharge la liste
            return cs.map(c => c.id === d.channel_id ? { ...c, last_message: d.message.content || (d.message.media_type ? `[${d.message.media_type}]` : ''), last_at: d.message.created_at, unread: isActive ? 0 : (d.message.user_id === me?.user_id ? (c.unread || 0) : (c.unread || 0) + 1) } : c);
          });
          setTyping(t => { const n = { ...t }; delete n[d.message.user_id]; return n; });
        } else if (d.type === 'message_deleted') {
          if (d.channel_id === activeIdRef.current) setMessages(prev => prev.map(m => m.id === d.message_id ? { ...m, deleted: 1, content: '' } : m));
        } else if (d.type === 'typing') {
          if (d.channel_id === activeIdRef.current && d.user.user_id !== me?.user_id)
            setTyping(t => ({ ...t, [d.user.user_id]: { name: d.user.display_name, t: Date.now() } }));
        } else if (d.type === 'poll_update') {
          if (d.channel_id === activeIdRef.current) setMessages(prev => prev.map(m => m.poll && m.poll.id === d.poll.id ? { ...m, poll: { ...d.poll, my_vote: m.poll.my_vote } } : m));
        } else if (d.type === 'reaction_update') {
          if (d.channel_id === activeIdRef.current) setMessages(prev => prev.map(m => m.id === d.message_id ? { ...m, reactions: { counts: d.counts, mine: m.reactions?.mine || [] } } : m));
        } else if (d.type === 'presence') {
          setOnline(d.online || []);
        } else if (d.type === 'pin') {
          loadChannels();
        } else if (d.type === 'channels_changed') {
          loadChannels();
          if (activeIdRef.current) getChatMembers(activeIdRef.current).then(r => setMembers(r.data)).catch(() => {});
        }
      };
      ws.onclose = () => { if (!stop) setTimeout(connect, 3000); };
    };
    connect();
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (!ws || ws.readyState >= WebSocket.CLOSING) connect();  // WS morte (veille/réseau) → reconnexion
      loadChannels();                                            // rattrape les messages manqués dans tous les cas
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      stop = true;
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      wsRef.current?.close();
    };
  }, [me, loadChannels]);

  const activeIdRef = useRef<number | null>(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  useEffect(() => {
    const i = setInterval(() => setTyping(t => {
      const n: typeof t = {}; const now = Date.now();
      for (const k in t) if (now - t[Number(k)].t < 4000) n[Number(k)] = t[Number(k)];
      return n;
    }), 1500);
    return () => clearInterval(i);
  }, []);

  const send = async () => {
    const c = text.trim();
    if (!c || !activeId || !canPost) return;
    setText(''); setMention(null);
    try { const r = await sendChatMessage(activeId, c); setMessages(prev => prev.some(m => m.id === r.data.id) ? prev : [...prev, r.data]); } catch { setText(c); }
  };

  // ── @mentions : autocomplétion sur les membres du canal ──
  const mentionList = (() => {
    if (!mention) return [] as ChatMember[];
    const q = mention.q.toLowerCase();
    return members
      .filter(m => m.user_id !== me?.user_id)
      .filter(m => !q || (m.username || '').toLowerCase().includes(q) || (m.display_name || '').toLowerCase().includes(q))
      .slice(0, 6);
  })();

  const onComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value; setText(v); onType();
    const caret = e.target.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = before.match(/(?:^|\s)@([A-Za-z0-9._]*)$/);
    if (m) { setMention({ q: m[1], start: caret - m[1].length - 1 }); setMentionIdx(0); }
    else setMention(null);
  };

  const pickMention = (mem: ChatMember) => {
    if (!mention) return;
    const handle = (mem.username || mem.display_name || '').replace(/^@/, '');
    const v = text;
    const caret = composerRef.current?.selectionStart ?? mention.start + mention.q.length + 1;
    const next = v.slice(0, mention.start) + '@' + handle + ' ' + v.slice(caret);
    setText(next); setMention(null);
    requestAnimationFrame(() => {
      const el = composerRef.current; if (!el) return;
      const pos = mention.start + handle.length + 2; el.focus(); el.setSelectionRange(pos, pos);
    });
  };

  const onComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && mentionList.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, mentionList.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentionList[mentionIdx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f || !activeId) return;
    setUploading(true);
    try {
      const up = await uploadChatMedia(activeId, f);
      const r = await sendChatMessage(activeId, text.trim(), up.data);
      setText('');
      setMessages(prev => prev.some(m => m.id === r.data.id) ? prev : [...prev, r.data]);
    } catch (err: any) { alert(err?.response?.data?.detail || "Échec de l'envoi du média."); }
    finally { setUploading(false); }
  };

  const vote = async (pollId: number, optionId: number) => {
    setMessages(prev => prev.map(m => m.poll && m.poll.id === pollId ? { ...m, poll: { ...m.poll, my_vote: optionId } } : m));
    try { const r = await voteChatPoll(pollId, optionId); setMessages(prev => prev.map(m => m.poll && m.poll.id === pollId ? { ...m, poll: r.data } : m)); } catch {}
  };

  const react = async (messageId: number, emoji: string) => {
    setReactFor(null);
    try {
      const r = await reactChatMessage(messageId, emoji);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: { counts: r.data.counts, mine: toggleArr(m.reactions?.mine || [], emoji) } } : m));
    } catch {}
  };

  useEffect(() => {
    if (!searchOpen || !activeId || !searchQ.trim()) { setSearchRes([]); return; }
    const t = setTimeout(() => searchChatMessages(activeId, searchQ.trim()).then(r => setSearchRes(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [searchOpen, searchQ, activeId]);

  const onType = () => {
    const now = Date.now();
    if (now - lastTypingSent.current > 1800 && wsRef.current?.readyState === 1 && activeId) {
      lastTypingSent.current = now;
      wsRef.current.send(JSON.stringify({ type: 'typing', channel_id: activeId }));
    }
  };

  const typers = Object.values(typing).map(x => x.name);
  const channelsByKind = { support: channels.filter(c => c.kind === 'support'), dm: channels.filter(c => c.kind === 'dm'), channel: channels.filter(c => c.kind === 'channel'), group: channels.filter(c => c.kind === 'group') };
  const showList = !isMobile || !mobileConv;
  const showConv = !isMobile || mobileConv;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      {showList && (
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: isMobile ? '10px 14px' : '16px 24px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <img src={AXO_IMG} alt="Axo" draggable={false} style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 1px 4px rgba(124,58,237,0.3)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#0f172a' }}>NS'Live Chat</div>
          {!isMobile && <div style={{ fontSize: 12, color: '#94a3b8' }}>Avec Axo, ton compagnon de l'agence</div>}
        </div>
      </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {showList && (
          <div style={{ width: isMobile ? '100%' : 240, flexShrink: 0, borderRight: isMobile ? undefined : '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {channels.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, padding: 16, textAlign: 'center' }}>Aucune discussion. Utilise « + Nouveau » en bas.</div>}
            {(['support', 'dm', 'channel', 'group'] as const).map(kind => channelsByKind[kind].length > 0 && (
              <div key={kind}>
                <div style={{ fontSize: 11, color: '#94a3b8', padding: '8px 8px 4px', fontWeight: 600 }}>{kind === 'channel' ? 'Canaux' : kind === 'dm' ? 'Messages privés' : kind === 'support' ? '🌐 Visiteurs (vitrine)' : 'Groupes'}</div>
                {channelsByKind[kind].map(c => (
                  <button key={c.id} onClick={() => { setActiveId(c.id); if (isMobile) setMobileConv(true); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: activeId === c.id ? '#eff6ff' : 'transparent', marginBottom: 2 }}>
                    {(c.kind === 'dm' || c.kind === 'support') ? <Avatar url={chanAvatar(c)} name={chanName(c)} />
                      : <span style={{ width: 30, height: 30, borderRadius: 9, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>{chanEmoji(c)}</span>}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: activeId === c.id ? '#1d4ed8' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chanName(c)}{c.kind === 'support' && <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: '#0ea5e9', borderRadius: 6, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>VISITEUR</span>}</span>
                      <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.last_message || (c.kind === 'dm' ? 'Message privé' : c.kind === 'support' ? `@${c.guest_handle}` : `${c.member_count || 0} membres`)}</span>
                    </span>
                    {!!c.unread && <span style={{ background: '#7c3aed', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 9, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>{c.unread}</span>}
                    {!c.unread && !!c.online && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />}
                  </button>
                ))}
              </div>
            ))}
            </div>

            {/* Bouton + Nouveau en bas de la liste (cohérent avec les autres modules) */}
            <div style={{ padding: 8, borderTop: '1px solid #e2e8f0', position: 'relative', flexShrink: 0 }}>
              {newMenu && (
                <>
                  <div onClick={() => setNewMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', left: 8, right: 8, bottom: '100%', marginBottom: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.16)', zIndex: 50, overflow: 'hidden' }}>
                    <button onClick={() => { setNewMenu(false); setShowDm(true); }} style={menuItem}>💬 Message privé</button>
                    <button onClick={() => { setNewMenu(false); setNewGroupOnly(true); setShowNew(true); }} style={menuItem}>👥 Nouveau groupe</button>
                    {isAdmin && <button onClick={() => { setNewMenu(false); setNewGroupOnly(false); setShowNew(true); }} style={menuItem}>📣 Nouveau canal (diffusion)</button>}
                  </div>
                </>
              )}
              <button onClick={() => setNewMenu(m => !m)} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: PURPLE, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Nouveau</button>
            </div>
          </div>
        )}

        {showConv && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {!active ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 12, padding: 24 }}>
                <img src={AXO_IMG} alt="Axo" draggable={false} style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 6px 20px rgba(124,58,237,0.28)' }} />
                <div style={{ fontSize: 15, color: '#475569', textAlign: 'center' }}>Choisis un canal à gauche pour discuter.<br />Axo t'y attend 💜</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                  {isMobile && <button onClick={() => setMobileConv(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>‹</button>}
                  {(active.kind === 'dm' || active.kind === 'support')
                    ? <Avatar url={chanAvatar(active)} name={chanName(active)} />
                    : <span style={{ fontSize: 16 }}>{chanEmoji(active)}</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{chanName(active)}{active.kind === 'support' && <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: '#0ea5e9', borderRadius: 6, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>VISITEUR</span>}</div>
                    <div style={{ fontSize: 11, color: '#16a34a' }}>
                      {active.kind === 'dm'
                        ? (online.includes(active.dm_peer?.user_id || -1) ? 'En ligne' : 'Hors ligne')
                        : active.kind === 'support'
                        ? <span style={{ color: '#0284c7' }}>@{active.guest_handle} · via la vitrine</span>
                        : `${members.filter(m => online.includes(m.user_id)).length} en ligne · ${members.length} membres`}
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setNotifMenu(m => !m)} title="Notifications de ce canal"
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 9px', cursor: 'pointer', color: active.notif_mode === 'muted' ? '#dc2626' : active.notif_mode && active.notif_mode !== 'default' ? '#7c3aed' : '#64748b', fontSize: 13 }}>
                      {active.notif_mode === 'muted' ? '🔕' : active.notif_mode === 'mentions' ? '🔔@' : '🔔'}
                    </button>
                    {notifMenu && (
                      <>
                        <div onClick={() => setNotifMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                        <div style={{ position: 'absolute', right: 0, top: '112%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 50, minWidth: 230, overflow: 'hidden' }}>
                          <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700, padding: '9px 12px 4px', textTransform: 'uppercase' }}>Notifications de ce canal</div>
                          {([
                            ['default', '⚙️ Par défaut (mon profil)'],
                            ['all', '🔔 Tous les messages'],
                            ['mentions', '🔔@ Seulement les mentions (@)'],
                            ['muted', '🔕 En sourdine'],
                          ] as const).map(([mode, label]) => {
                            const cur = active.notif_mode || 'default';
                            return (
                              <button key={mode} onClick={() => {
                                setNotifMenu(false);
                                setChannels(cs => cs.map(c => c.id === active.id ? { ...c, notif_mode: mode } : c));
                                setChatNotifMode(active.id, mode).catch(() => {});
                              }} style={{ ...menuItem, display: 'flex', alignItems: 'center', gap: 8, fontWeight: cur === mode ? 700 : 400, background: cur === mode ? '#f5f3ff' : 'none', color: cur === mode ? '#7c3aed' : '#0f172a' }}>
                                {cur === mode ? '✓' : <span style={{ width: 10 }} />} {label}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                  <button onClick={() => { setSearchOpen(s => !s); setSearchQ(''); }} title="Rechercher" style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 9px', cursor: 'pointer', color: searchOpen ? '#7c3aed' : '#64748b', fontSize: 13 }}>🔍</button>
                  {active.kind !== 'dm' && active.kind !== 'support' && <button onClick={() => setShowMembers(s => !s)} title="Membres" style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 9px', cursor: 'pointer', color: '#64748b', fontSize: 13 }}>👥 {members.length}</button>}
                </div>

                {searchOpen && (
                  <div style={{ padding: '8px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                    <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Rechercher dans ce canal…" style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 18, padding: '8px 14px', fontSize: 13, outline: 'none' }} />
                  </div>
                )}

                {active.pinned_message && !searchOpen && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', background: '#f5f3ff', borderBottom: '1px solid #ede9fe', flexShrink: 0 }}>
                    <span style={{ fontSize: 13 }}>📌</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10.5, color: '#7c3aed', fontWeight: 700 }}>Message épinglé · {active.pinned_message.display_name}</div>
                      <div style={{ fontSize: 12.5, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{active.pinned_message.content || (active.pinned_message.media_type ? `[${active.pinned_message.media_type}]` : '')}</div>
                    </div>
                    {(isAdmin || myMember?.member_role === 'moderator') && (
                      <button onClick={() => pinChatMessage(active.id, null).catch(() => {})} title="Détacher" style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 15 }}>×</button>
                    )}
                  </div>
                )}

                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {searchOpen && searchQ.trim() ? (
                        searchRes.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>Aucun résultat.</div>
                          : searchRes.map(m => (
                            <div key={m.id} style={{ display: 'flex', gap: 8, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                              <Avatar url={m.avatar_url} name={m.display_name} small />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{m.display_name} · {timeFmt(m.created_at)}</div>
                                <div style={{ fontSize: 13, color: '#0f172a' }}>{m.content}</div>
                              </div>
                            </div>
                          ))
                      ) : messages.map(m => {
                        const mine = m.user_id === me?.user_id;
                        return (
                          <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexDirection: mine ? 'row-reverse' : 'row' }}>
                            <Avatar url={m.avatar_url} name={m.display_name} />
                            <div style={{ maxWidth: '76%' }}>
                              {!mine && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{m.display_name} · {timeFmt(m.created_at)}</div>}
                              <div style={{ position: 'relative', background: m.deleted ? '#f1f5f9' : mine ? '#ede9fe' : '#fff', border: '1px solid ' + (mine ? '#ddd6fe' : '#e2e8f0'), borderRadius: mine ? '12px 0 12px 12px' : '0 12px 12px 12px', padding: '8px 11px', fontSize: 13.5, color: m.deleted ? '#94a3b8' : '#0f172a', fontStyle: m.deleted ? 'italic' : 'normal', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                {m.deleted ? 'Message supprimé' : (
                                  m.poll ? <PollView poll={m.poll} onVote={o => vote(m.poll!.id, o)} /> : (
                                    <>
                                      {m.media_url && <div style={{ marginBottom: m.content ? 6 : 0 }}><MediaView msg={m} /></div>}
                                      {m.content && <span>{linkify(m.content)}</span>}
                                    </>
                                  )
                                )}
                                {!m.deleted && (
                                  <div className="msg-del" style={{ position: 'absolute', top: -9, [mine ? 'left' : 'right']: -8, gap: 3 } as any}>
                                    <div style={{ position: 'relative' }}>
                                      <button onClick={() => setReactFor(f => f === m.id ? null : m.id)} title="Réagir" style={pinBtnStyle}>😊</button>
                                      {reactFor === m.id && (
                                        <div style={{ position: 'absolute', top: 22, [mine ? 'left' : 'right']: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: '4px 6px', display: 'flex', gap: 2, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', zIndex: 20 } as any}>
                                          {REACT_EMOJIS.map(e => <button key={e} onClick={() => react(m.id, e)} style={{ background: 'none', border: 'none', fontSize: 17, cursor: 'pointer', padding: 1 }}>{e}</button>)}
                                        </div>
                                      )}
                                    </div>
                                    {(isAdmin || myMember?.member_role === 'moderator') && (
                                      <button onClick={() => pinChatMessage(active!.id, m.id).catch(() => {})} title="Épingler" style={pinBtnStyle}>📌</button>
                                    )}
                                    {(mine || isAdmin || myMember?.member_role === 'moderator') && (
                                      <button onClick={() => deleteChatMessage(m.id).catch(() => {})} title="Supprimer" style={{ ...pinBtnStyle, border: '1px solid #fecaca', color: '#dc2626' }}>×</button>
                                    )}
                                  </div>
                                )}
                              </div>
                              {m.reactions && Object.keys(m.reactions.counts).length > 0 && (
                                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                                  {Object.entries(m.reactions.counts).map(([e, n]) => (
                                    <button key={e} onClick={() => react(m.id, e)} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, padding: '1px 7px', borderRadius: 11, cursor: 'pointer', border: '1px solid', borderColor: m.reactions!.mine.includes(e) ? '#c4b5fd' : '#e2e8f0', background: m.reactions!.mine.includes(e) ? '#f5f3ff' : '#fff' }}>{e} <span style={{ color: '#64748b' }}>{n}</span></button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {typers.length > 0 && (
                        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', paddingLeft: 4 }}>
                          {typers.slice(0, 2).join(', ')} {typers.length > 1 ? 'écrivent' : 'est en train d\'écrire'}…
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: '1px solid #e2e8f0', padding: 8, paddingBottom: isMobile ? 'calc(8px + env(safe-area-inset-bottom, 0px))' : 8, background: '#fff', flexShrink: 0 }}>
                      {myMember?.muted ? (
                        <div style={{ textAlign: 'center', color: '#dc2626', fontSize: 12.5, padding: '8px 0' }}>🔇 Tu es en sourdine dans ce canal.</div>
                      ) : canPost ? (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, position: 'relative' }}>
                          {mention && mentionList.length > 0 && (
                            <div style={{ position: 'absolute', bottom: 46, left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 15, maxHeight: 230, overflowY: 'auto' }}>
                              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700, padding: '4px 8px' }}>Notifier un membre</div>
                              {mentionList.map((mem, i) => (
                                <div key={mem.user_id} onMouseDown={e => { e.preventDefault(); pickMention(mem); }}
                                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', background: i === mentionIdx ? '#f5f3ff' : 'transparent' }}>
                                  <Avatar url={mem.avatar_url} name={mem.display_name} small />
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mem.display_name}</span>
                                    {mem.username && <span style={{ display: 'block', fontSize: 11, color: '#7c3aed' }}>@{mem.username}</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {showEmoji && (
                            <div style={{ position: 'absolute', bottom: 46, left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 8, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10, width: 280 }}>
                              {EMOJIS.map(e => <button key={e} onClick={() => { setText(t => t + e); setShowEmoji(false); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 3, borderRadius: 6 }}>{e}</button>)}
                            </div>
                          )}
                          <button onClick={() => setShowEmoji(s => !s)} title="Emoji" style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>😊</button>
                          <input ref={fileRef} type="file" accept="image/*,video/*,audio/*,.gif,.pdf,.zip" style={{ display: 'none' }} onChange={handleFile} />
                          <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Joindre un média" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: uploading ? '#cbd5e1' : '#94a3b8' }}>{uploading ? '⏳' : '📎'}</button>
                          <button onClick={() => setShowPoll(true)} title="Créer un sondage" style={{ background: 'none', border: 'none', fontSize: 19, cursor: 'pointer', color: '#94a3b8' }}>📊</button>
                          <textarea ref={composerRef} value={text} onChange={onComposerChange} onKeyDown={onComposerKey}
                            placeholder="Écris un message… (@ pour notifier)" rows={1}
                            style={{ flex: 1, resize: 'none', border: '1px solid #e2e8f0', borderRadius: 18, padding: '9px 14px', fontSize: 13.5, outline: 'none', maxHeight: 100, fontFamily: 'inherit' }} />
                          <button onClick={send} disabled={!text.trim()} style={{ width: 38, height: 38, borderRadius: '50%', background: text.trim() ? PURPLE : '#cbd5e1', border: 'none', color: '#fff', fontSize: 17, cursor: text.trim() ? 'pointer' : 'default', flexShrink: 0 }}>➤</button>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12.5, padding: '8px 0' }}>🔒 Canal en lecture seule — seuls les modérateurs publient ici.</div>
                      )}
                    </div>
                  </div>

                  {showMembers && !isMobile && (
                    <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid #e2e8f0', background: '#fff', overflowY: 'auto', padding: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>Membres ({members.length})</span>
                        <button onClick={() => setShowMembers(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>×</button>
                      </div>
                      {(isAdmin || myMember?.member_role === 'moderator') && <MemberManager channelId={active.id} channel={active} isAdmin={isAdmin} members={members} onChange={() => { getChatMembers(active.id).then(r => setMembers(r.data)); loadChannels(); }} />}
                      {!(isAdmin || myMember?.member_role === 'moderator') && members.map(m => (
                        <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px' }}>
                          <Avatar url={m.avatar_url} name={m.display_name} small />
                          <span style={{ flex: 1, fontSize: 12.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name}</span>
                          {online.includes(m.user_id) && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showNew && <NewChannelModal groupOnly={newGroupOnly} isAdmin={isAdmin} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadChannels(); }} />}
      {showDm && <NewDmModal onClose={() => setShowDm(false)} onOpen={async (uid) => {
        try { const r = await openChatDm(uid); setShowDm(false); await loadChannels(); setActiveId(r.data.id); if (isMobile) setMobileConv(true); } catch {}
      }} />}
      {showPoll && activeId && <NewPollModal onClose={() => setShowPoll(false)} onCreate={async (q, opts) => {
        try { const r = await createChatPoll(activeId, q, opts); setMessages(prev => prev.some(m => m.id === r.data.id) ? prev : [...prev, r.data]); setShowPoll(false); } catch (e: any) { alert(e?.response?.data?.detail || 'Erreur sondage.'); }
      }} />}

      {/* Membres — plein écran sur mobile */}
      {showMembers && isMobile && active && active.kind !== 'dm' && active.kind !== 'support' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#fff', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
            <button onClick={() => setShowMembers(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer' }}>‹</button>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Membres · {chanName(active)}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
            {(isAdmin || myMember?.member_role === 'moderator') && <MemberManager channelId={active.id} channel={active} isAdmin={isAdmin} members={members} onChange={() => { getChatMembers(active.id).then(r => setMembers(r.data)); loadChannels(); }} />}
            {!(isAdmin || myMember?.member_role === 'moderator') && members.map(m => (
              <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 2px', borderBottom: '1px solid #f1f5f9' }}>
                <Avatar url={m.avatar_url} name={m.display_name} />
                <span style={{ flex: 1, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name}</span>
                {online.includes(m.user_id) && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }} />}
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`.msg-del{display:none}div:hover>.msg-del,div:hover>div>.msg-del{display:flex !important}`}</style>
    </div>
  );
}

function Avatar({ url, name, small }: { url?: string | null; name?: string; small?: boolean }) {
  const [err, setErr] = useState(false);
  const s = small ? 24 : 30;
  const proxied = url && /^https?:\/\//i.test(url) ? `/api/gift-image?url=${encodeURIComponent(url)}` : url;
  return (
    <div style={{ width: s, height: s, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', fontWeight: 600, fontSize: small ? 10 : 11, flexShrink: 0, overflow: 'hidden' }}>
      {proxied && !err ? <img src={proxied} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} /> : initials(name)}
    </div>
  );
}

function MemberManager({ channelId, members, onChange, channel, isAdmin }: { channelId: number; members: ChatMember[]; onChange: () => void; channel?: ChatChannel; isAdmin?: boolean }) {
  const [everyone, setEveryone] = useState(!!channel?.audience_everyone);
  const [teams, setTeams] = useState<Set<string>>(new Set(channel?.teams || []));
  useEffect(() => { setEveryone(!!channel?.audience_everyone); setTeams(new Set(channel?.teams || [])); }, [channel?.id, channel?.audience_everyone, JSON.stringify(channel?.teams)]);
  const byId = new Map(members.map(m => [m.user_id, m]));
  const memberIds = new Set(members.map(m => m.user_id));
  const saveEveryone = (v: boolean) => { setEveryone(v); updateChatChannel(channelId, { everyone: v }).then(onChange).catch(() => {}); };
  const toggleTeam = (g: string, v: boolean) => { const n = new Set(teams); v ? n.add(g) : n.delete(g); setTeams(n); updateChatChannel(channelId, { teams: Array.from(n) }).then(onChange).catch(() => {}); };
  const toggleMember = (uid: number, v: boolean) => { (v ? addChatMember(channelId, uid) : removeChatMember(channelId, uid)).then(onChange).catch(() => {}); };
  const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, flexShrink: 0 };
  return (
    <AudienceTree
      everyone={everyone} teams={teams} memberIds={memberIds} canGrantBroad={isAdmin}
      onEveryone={saveEveryone} onTeam={toggleTeam} onMember={toggleMember}
      renderExtra={m => {
        const cm = byId.get(m.user_id);
        if (!cm) return null;
        return (
          <>
            <button onClick={e => { e.stopPropagation(); muteChatMember(channelId, m.user_id, !cm.muted).then(onChange); }} title={cm.muted ? 'Réactiver' : 'Sourdine'} style={{ ...iconBtn, color: cm.muted ? '#dc2626' : '#cbd5e1' }}>🔇</button>
            <button onClick={e => { e.stopPropagation(); addChatMember(channelId, m.user_id, cm.member_role === 'moderator' ? 'member' : 'moderator').then(onChange); }} title="Modérateur" style={{ ...iconBtn, color: cm.member_role === 'moderator' ? '#7c3aed' : '#cbd5e1' }}>🛡️</button>
          </>
        );
      }}
    />
  );
}

function NewChannelModal({ onClose, onCreated, groupOnly, isAdmin }: { onClose: () => void; onCreated: () => void; groupOnly?: boolean; isAdmin?: boolean }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'channel' | 'group'>('group');
  const [policy, setPolicy] = useState<'all' | 'restricted'>('all');
  const [emoji, setEmoji] = useState('');
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [everyone, setEveryone] = useState(false);
  const [teams, setTeams] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await createChatChannel({ name: name.trim(), kind, post_policy: policy, emoji: emoji || null, member_ids: Array.from(sel), everyone, teams: Array.from(teams) }); onCreated(); }
    catch { setBusy(false); }
  };
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto', padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>{groupOnly ? 'Nouveau groupe' : 'Nouvel espace'}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={emoji} onChange={e => setEmoji(e.target.value.slice(0, 2))} placeholder="🙂" style={{ width: 52, textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 0', fontSize: 18 }} />
          <input value={name} onChange={e => setName(e.target.value)} placeholder={groupOnly ? 'Nom du groupe' : "Nom de l'espace"} style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14 }} />
        </div>
        {!groupOnly && <Seg label="Type" value={kind} onChange={v => setKind(v as any)} options={[['group', '👥 Groupe (discussion)'], ['channel', '# Canal (diffusion)']]} />}
        <Seg label="Qui peut publier ?" value={policy} onChange={v => setPolicy(v as any)} options={[['all', 'Tous les membres'], ['restricted', 'Admin + modérateurs']]} />

        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '14px 0 6px' }}>Qui a accès ?</div>
        <AudienceTree
          everyone={everyone} teams={teams} memberIds={sel} canGrantBroad={isAdmin}
          onEveryone={setEveryone}
          onTeam={(g, v) => setTeams(s => { const n = new Set(s); v ? n.add(g) : n.delete(g); return n; })}
          onMember={(uid, v) => setSel(s => { const n = new Set(s); v ? n.add(uid) : n.delete(uid); return n; })}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('#64748b', false)}>Annuler</button>
          <button onClick={create} disabled={busy || !name.trim()} style={btn('#7c3aed', true)}>{busy ? '…' : 'Créer'}</button>
        </div>
      </div>
    </div>
  );
}

function NewDmModal({ onClose, onOpen }: { onClose: () => void; onOpen: (userId: number) => void }) {
  const [users, setUsers] = useState<ChatMember[]>([]);
  const [q, setQ] = useState('');
  useEffect(() => { getChatUsers().then(r => setUsers(r.data)).catch(() => {}); }, []);
  const list = users.filter(u => u.display_name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>💬 Message privé</div>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher une personne…" style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14, marginBottom: 10 }} />
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #e2e8f0', borderRadius: 8 }}>
          {list.map(u => (
            <div key={u.user_id} onClick={() => onOpen(u.user_id)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
              <Avatar url={u.avatar_url} name={u.display_name} small />
              <span style={{ fontSize: 13.5, color: '#0f172a' }}>{u.display_name}</span>
            </div>
          ))}
          {list.length === 0 && <div style={{ padding: 14, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>Personne trouvé.</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button onClick={onClose} style={btn('#64748b', false)}>Fermer</button></div>
      </div>
    </div>
  );
}

function NewPollModal({ onClose, onCreate }: { onClose: () => void; onCreate: (q: string, opts: string[]) => void }) {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState<string[]>(['', '']);
  const valid = q.trim() && opts.filter(o => o.trim()).length >= 2;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>📊 Nouveau sondage</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ta question" style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14, marginBottom: 10 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {opts.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 6 }}>
              <input value={o} onChange={e => setOpts(s => s.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: 13 }} />
              {opts.length > 2 && <button onClick={() => setOpts(s => s.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>×</button>}
            </div>
          ))}
        </div>
        {opts.length < 10 && <button onClick={() => setOpts(s => [...s, ''])} style={{ ...btn('#7c3aed', false), marginTop: 8, fontSize: 12 }}>+ Ajouter une option</button>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('#64748b', false)}>Annuler</button>
          <button onClick={() => valid && onCreate(q.trim(), opts.map(o => o.trim()).filter(Boolean))} disabled={!valid} style={{ ...btn('#7c3aed', true), opacity: valid ? 1 : 0.5 }}>Publier</button>
        </div>
      </div>
    </div>
  );
}

function Seg({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {options.map(([v, l]) => (
          <button key={v} onClick={() => onChange(v)} style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: '1px solid', borderColor: value === v ? '#7c3aed' : '#e2e8f0', background: value === v ? '#f5f3ff' : '#fff', color: value === v ? '#7c3aed' : '#64748b', fontSize: 12, fontWeight: value === v ? 700 : 500, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function btn(color: string, filled: boolean): React.CSSProperties {
  return { padding: '8px 14px', borderRadius: 8, border: '1px solid ' + color, background: filled ? color : '#fff', color: filled ? '#fff' : color, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
}

const pinBtnStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: 20, height: 20, fontSize: 10, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const menuItem: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', cursor: 'pointer' };
